import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { PageContainer } from '@/components/common/PageContainer';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { KpiCard } from '@/components/features/dashboard/KpiCard';
import { Inbox, PackageCheck, Warehouse, Truck, Send, RotateCcw, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { IncomingManifestsCard } from '@/components/fc/IncomingManifestsCard';
import { OutgoingManifestsCard } from '@/components/fc/OutgoingManifestsCard';

interface FcNode {
  id: string;
  name: string;
  code: string;
  city?: { id: string; name_translations?: { ar?: string; en?: string } };
}

interface FcStats {
  node: { id: string; name: string; code: string; city_id: string | null };
  counts: { PKM: number; RITS: number; INV: number; STD: number; OTD: number; RTS: number };
}

const KPI_DEFS = [
  { key: 'PKM',  title: 'الاستلامات قيد المعالجة',  icon: Inbox,        gradient: 'from-blue-500 to-blue-700' },
  { key: 'RITS', title: 'الوارد اليوم',                icon: PackageCheck, gradient: 'from-green-500 to-emerald-700' },
  { key: 'INV',  title: 'المخزون الحالي',              icon: Warehouse,    gradient: 'from-purple-500 to-purple-700' },
  { key: 'STD',  title: 'جاهز للتوصيل',                 icon: Truck,        gradient: 'from-orange-500 to-orange-700' },
  { key: 'OTD',  title: 'خارج للتوصيل',                 icon: Send,         gradient: 'from-indigo-500 to-indigo-700' },
  { key: 'RTS',  title: 'المرتجعات',                    icon: RotateCcw,    gradient: 'from-rose-500 to-rose-700' },
] as const;

export default function FCDashboard() {
  const { nodeId: urlNodeId } = useParams<{ nodeId?: string }>();
  const navigate = useNavigate();
  const { get } = useApi();

  const [fcs, setFcs] = useState<FcNode[]>([]);
  const [selectedId, setSelectedId] = useState<string | undefined>(urlNodeId);
  const [stats, setStats] = useState<FcStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);

  // Load FC list (filtered to current carrier server-side via JWT)
  const loadFcs = useCallback(async () => {
    try {
      const res: any = await get('/api/v1/dashboard/nodes?type=fulfillment_center,hub');
      const list: FcNode[] = Array.isArray(res) ? res : (res?.data ?? []);
      setFcs(list);
      if (!selectedId && list.length > 0) {
        setSelectedId(list[0].id);
      } else if (selectedId && !list.find((n) => n.id === selectedId)) {
        // URL nodeId no longer valid → fall back to first FC
        if (list.length > 0) setSelectedId(list[0].id);
      }
    } catch {
      toast.error('فشل في جلب مراكز الفرز');
    } finally {
      setLoading(false);
    }
  }, [get, selectedId]);

  useEffect(() => { loadFcs(); }, [loadFcs]);

  // Load stats for selected FC
  const loadStats = useCallback(async (id: string) => {
    setStatsLoading(true);
    try {
      const res: any = await get(`/api/v1/dashboard/nodes/${id}/fc-stats`);
      const data = res?.data ?? res;
      setStats(data);
    } catch (e: any) {
      const msg = e?.response?.data?.message;
      toast.error(msg || 'فشل في جلب إحصائيات المركز');
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, [get]);

  useEffect(() => {
    if (selectedId) {
      loadStats(selectedId);
      // Keep URL in sync for sharing/bookmarking, but don't push history
      if (selectedId !== urlNodeId) {
        navigate(`/dashboard/fc/${selectedId}`, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  if (loading) return <PageContainer><LoadingSkeleton type="card" rows={6} /></PageContainer>;

  if (fcs.length === 0) {
    return (
      <PageContainer>
        <div className="text-center py-16" dir="rtl">
          <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">لا توجد مراكز فرز معينة لشركتك</p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="flex flex-col gap-6" dir="rtl">
        {/* Header with FC selector */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold mb-1">عمليات مركز الفرز</h1>
            <p className="text-sm text-muted-foreground">نظرة عامة على عمليات المركز</p>
          </div>
          <div className="min-w-[260px]">
            <Select value={selectedId} onValueChange={setSelectedId}>
              <SelectTrigger>
                <SelectValue placeholder="اختر مركز الفرز" />
              </SelectTrigger>
              <SelectContent>
                {fcs.map((n) => (
                  <SelectItem key={n.id} value={n.id}>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <span>{n.name}</span>
                      <span className="font-mono text-xs text-muted-foreground">({n.code})</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid gap-3 md:gap-4 grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          {KPI_DEFS.map((def) => (
            <KpiCard
              key={def.key}
              title={def.title}
              value={statsLoading ? '...' : (stats?.counts[def.key as keyof FcStats['counts']] ?? 0)}
              icon={def.icon}
              gradient={def.gradient}
              format="number"
            />
          ))}
        </div>

        {/* Receiving + Dispatch sessions */}
        {selectedId && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <IncomingManifestsCard key={`in-${selectedId}`} nodeId={selectedId} />
            <OutgoingManifestsCard key={`out-${selectedId}`} nodeId={selectedId} />
          </div>
        )}
      </div>
    </PageContainer>
  );
}
